'use client';

import { createTRPCReact } from '@trpc/react-query';
import {
  createTRPCClient as createVanillaClient,
  httpBatchLink,
  type TRPCLink,
} from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from '@impact/api/trpc';
import { useTenantStore } from '@/stores/tenant-store';
import { getTurnstileToken, isTurnstileConfigured } from '@/lib/turnstile';

/**
 * tRPC Clients for Impact Admin
 *
 * We maintain TWO tRPC clients for different use cases:
 *
 * 1. `trpc` (React client) - For use inside React components with hooks
 *    - Uses React Query for caching, loading states, refetching
 *    - Example: `trpc.events.list.useQuery()`
 *    - Use this for: Component data fetching, mutations with optimistic updates
 *
 * 2. `trpcVanilla` (Vanilla client) - For imperative calls outside React hooks
 *    - Direct async/await calls without React Query
 *    - Example: `await trpcVanilla.authMe.me.query()`
 *    - Use this for: Event handlers, callbacks, non-component code
 *
 * WHY both exist:
 * React hooks (useQuery, useMutation) can only be called at component render level.
 * When you need to call tRPC inside an event handler (like after login success),
 * you need the vanilla client for imperative calls.
 *
 * Both clients share the same link configuration (DRY) for consistent behavior.
 */

/**
 * Mutations gated by the API's Turnstile CAPTCHA middleware
 * (publicTurnstileProcedure in apps/api/src/trpc/routers/public/donation.ts).
 * Keep in sync with the router — a missing entry here means the mutation is
 * sent without a token and rejected once TURNSTILE_ENFORCEMENT=enforce.
 */
const TURNSTILE_GATED_MUTATIONS = new Set([
  'publicDonation.createEntrantSession',
  'publicDonation.createTeamSession',
  'publicDonation.createRandomEntrantSession',
  'publicDonation.createEventSession',
  'publicDonation.createGeneralSession',
  'publicDonation.createRegistrationSession',
  'publicDonation.createCheckoutSession',
  'publicDonation.commitCheckout',
  'publicDonation.createStripeCheckout',
  'publicDonation.createStripeHostedCheckout',
]);

/**
 * Injects a fresh single-use Turnstile token into gated checkout mutations at
 * one choke point, so no call site needs token plumbing. On token failure the
 * mutation is still sent without a token: the API audit-logs it (log mode) or
 * rejects it with a CAPTCHA error the checkout UIs already surface (enforce).
 */
const turnstileLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    if (
      op.type !== 'mutation' ||
      !TURNSTILE_GATED_MUTATIONS.has(op.path) ||
      !isTurnstileConfigured()
    ) {
      return next(op);
    }

    return observable((observer) => {
      let unsubscribed = false;
      let subscription: { unsubscribe: () => void } | null = null;

      const forward = (token: string | null) => {
        if (unsubscribed) {
          return;
        }
        const input =
          token === null
            ? op.input
            : { ...(op.input as Record<string, unknown>), turnstileToken: token };
        subscription = next({ ...op, input }).subscribe(observer);
      };

      getTurnstileToken().then(
        (token) => forward(token),
        (error) => {
          console.warn('[turnstile] token unavailable, sending without one:', error);
          forward(null);
        },
      );

      return () => {
        unsubscribed = true;
        subscription?.unsubscribe();
      };
    });
  };
};

/**
 * Shared link configuration for all tRPC clients
 * Ensures consistent tenant headers and credentials across both clients
 */
function createLinks() {
  return [
    turnstileLink,
    httpBatchLink({
      // Use relative URL - requests go through Next.js proxy
      url: '/api/trpc',
      // Add headers dynamically on each request
      headers() {
        const tenantId = useTenantStore.getState().tenantId;
        return {
          ...(tenantId && { 'X-Tenant-ID': tenantId }),
        };
      },
      // Include credentials for authentication
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: 'include',
        });
      },
    }),
  ];
}

/**
 * React tRPC client - use with hooks in components
 *
 * @example
 * const { data } = trpc.events.list.useQuery();
 * const mutation = trpc.events.create.useMutation();
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Create React Query-integrated tRPC client
 * Used by TRPCProvider to initialize the React client
 */
export function createTRPCClient() {
  return trpc.createClient({ links: createLinks() });
}

/**
 * Vanilla tRPC client - use for imperative calls outside React hooks
 *
 * @example
 * // In an event handler after login:
 * const authContext = await trpcVanilla.authMe.me.query();
 *
 * // In a utility function:
 * const tenant = await trpcVanilla.tenant.get.query();
 */
export const trpcVanilla = createVanillaClient<AppRouter>({ links: createLinks() });
